import React, { useState, useRef } from "react";
import "./createLegalDoc.css";
import { logEvent } from "firebase/analytics";
import { config } from "../config";
import { useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import FullScreenLoader from "../components/FullScreenLoader";
import GoogleButton from "../components/GoogleAuth";
import { Helmet } from "react-helmet-async";
import LeftPanel from "../components/leftPanel";
import { analytics } from "../components/firebase";
const PropertyOwnership = () => {
  const googleAuthRef = useRef(null);
  const [buyerDetails, setBuyerDetails] = useState("");
  const [sellerDetails, setSellerDetails] = useState("");
  const [propertyAddress, setPropertyAddress] = useState("");
  const [propertyDescription, setPropertyDescription] = useState("");
  const [salePrice, setSalePrice] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("");
  const [closingDate, setClosingDate] = useState("");
  const [liensEncumbrances, setLiensEncumbrances] = useState("");
  const [language, setLanguage] = useState("English");
  const [errors, setErrors] = useState({});
  const [popupMessage] = useState("");
  const [isPopupVisible] = useState(false);
  const textAreaRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isButtonVisible, setIsButtonVisible] = useState(true);

  const navigate = useNavigate();
  React.useEffect(() => {
    document.documentElement.style.setProperty("--main-color", config.color);
  }, [config.color]);

  
  const location = useLocation();

  const initialText = location.state?.text || "";
  const receivedKey = location.state?.key || "Legal Document";
  const [Topic, setTopic] = useState(initialText);
  const [Keypoints, setKeypoints] = useState("");

  const handleBackClick = () => {
    navigate(-1);
  };

  const handleLoginSuccess = (userData) => {
    toast.success("Logged in – go ahead and click Generate again!");
  };

  const handleLoginFailure = () => {
    toast.error("Something went wrong, Please try again");
  };

  const handleGenerate = async () => {
    setIsLoading(true);
    setIsButtonVisible(false);
    document.body.classList.add("no-scroll");

    try {
      const requestData = {
        buyerDetails,
        sellerDetails,
        propertyAddress,
        propertyDescription,
        salePrice,
        paymentMethod,
        closingDate,
        liensEncumbrances,
        selectedLanguage: language,
      };
      logEvent(analytics, "generate_button_click", {
        action: "Generate Property Ownership Document",
        topic: Topic,
      });

      const generationResponse = await fetch(
        `${config.baseURL}/ownership_gen.php`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(requestData),
        }
      );

      const rawResponse = await generationResponse.text();
      let data;

      try {
        data = JSON.parse(rawResponse);
      } catch {
        toast.error("Please re-try after some time");
        return;
      }

      if (data.error) {
        if (data.error === "Authentication failed") {
          
          googleAuthRef.current?.login();
          return;
        }
        if (data.redirect === "premium") {
          navigate(`/premium?id=${data.premiumId}`);
          return;
        }
        toast.error("Please re-try after some time");
        return;
      }

      if (data.success && data.resultText) {
        navigate("/result", {
          state: { result: data.resultText, history_id: data.history_id },
        });
        setBuyerDetails("");
        setSellerDetails("");
        setPropertyAddress("");
        setPropertyDescription("");
        setSalePrice("");
        setPaymentMethod("");
        setClosingDate("");
        setLiensEncumbrances("");
        setLanguage("English");
      } else {
        toast.error("Please re-try after some time");
      }
    } catch (error) {
      toast.error("Please re-try after some time");
      logEvent(analytics, "error", {
        error_type: "property_api_failed",
        error_message: "Failed to generate document due to API error.",
        action: "generate_button_click",
      });
    } finally {
      setIsLoading(false);
      document.body.classList.remove("no-scroll");
    }
  };

  return (
    <>
     <Helmet key="property-ownership">
  <title>
    Property Ownership Legal Document Generator | AI-powered Document Creation
  </title>

  <meta
    name="description"
    content="Create property ownership legal documents with ease. Fill in the details such as buyer, seller, property address, and sale price to generate a customized, AI-powered legal document. Secure, fast, and lawyer-approved templates."
  />

  <meta
    name="keywords"
    content="property ownership legal document, property sale agreement, property ownership contract, AI legal document generator, customizable property contract, real estate legal document, buyer seller agreement, legal document creation online, property ownership transfer, AI-generated legal documents, real estate contract generator, document creation for property sale"
  />

  <link rel="canonical" href="https://legaldocumentgenerator.com/property-ownership-templates" />
</Helmet>
    <div className="create_background">
      
      {isLoading && <FullScreenLoader />}
      {isPopupVisible && (
        <div className="popup-notification">
          <p>{popupMessage}</p>
        </div>
      )}

      <LeftPanel />
      <div className="create_main_templates">
        <div className="create_title">
                  <img src={config.Backbutton} alt="back" onClick={handleBackClick} />
                  Create AI Legal Document
                </div>
        <div className="create_form_grid">
          <div className="create_inputbox1">
            Buyer's full name and details
            <textarea
              className="textarea_input"
              placeholder="e.g., John Smith, (123) 456-7890, johnsmith@gmail.com"
              value={buyerDetails}
              onChange={(e) => setBuyerDetails(e.target.value)}
              maxLength={1000}
            />
          </div>

          <div className="create_inputbox1">
            Seller's full name and details
            <textarea
              className="textarea_input"
              placeholder="e.g., Jane Doe, (987) 654-3290, janedoe@gmail.com"
              value={sellerDetails}
              onChange={(e) => setSellerDetails(e.target.value)}
              maxLength={1000}
            />
          </div>

          <div className="create_inputbox1">
            Property address
            <textarea
              className="textarea_input"
              placeholder="e.g., 123 Main Street, Apt 4B, Springfield, IL"
              value={propertyAddress}
              onChange={(e) => setPropertyAddress(e.target.value)}
              maxLength={1000}
            />
          </div>

          <div className="create_inputbox1">
            Property description (size, features)
            <textarea
              className="textarea_input"
              placeholder="e.g., 3-bed, 2-bath, 2000 sq.ft, pool, garage"
              value={propertyDescription}
              onChange={(e) => setPropertyDescription(e.target.value)}
              maxLength={1000}
            />
          </div>

          <div className="create_inputbox1">
            Sale price
            <textarea
              className="textarea_input"
              placeholder="e.g., $350,000"
              value={salePrice}
              onChange={(e) => setSalePrice(e.target.value)}
              maxLength={1000}
            />
          </div>

          <div className="create_inputbox1">
            Payment method
            <textarea
              className="textarea_input"
              placeholder="e.g., Bank transfer, mortgage, cash"
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value)}
              maxLength={1000}
            />
          </div>

          <div className="create_inputbox1">
            Closing date
            <textarea
              className="textarea_input"
              placeholder="e.g., December 31, 2024"
              value={closingDate}
              onChange={(e) => setClosingDate(e.target.value)}
              maxLength={1000}
            />
          </div>

          <div className="create_inputbox1">
            Any liens or encumbrances
            <textarea
              className="textarea_input"
              placeholder="e.g., None or list outstanding mortgages"
              value={liensEncumbrances}
              onChange={(e) => setLiensEncumbrances(e.target.value)}
              maxLength={1000}
            />
          </div>

          <div className="create_inputbox1">
            Select Language
            <select
              value={language}
              className="text_input"
              onChange={(e) => setLanguage(e.target.value)}
            >
              <option value="English">English</option>
              <option value="Arabic">Arabic (RTL)</option>
              <option value="Bulgarian">Bulgarian</option>
              <option value="Chinese">Chinese (Simplified)</option>
              <option value="French">French</option>
              <option value="German">German</option>
              <option value="Hebrew">Hebrew (RTL)</option>
              <option value="Hindi">Hindi</option>
              <option value="Indonesian">Indonesian</option>
              <option value="Italian">Italian</option>
              <option value="Japanese">Japanese</option>
              <option value="Portuguese">Portuguese</option>
              <option value="Punjabi">Punjabi</option>
              <option value="Russian">Russian</option>
              <option value="Spanish">Spanish</option>
              <option value="Swahili">Swahili</option>
              <option value="Tagalog">Tagalog</option>
              <option value="Urdu">Urdu</option>
            </select>
          </div>
        </div>
        <ToastContainer />
        <GoogleButton
          id="loginHidden"
          ref={googleAuthRef}
          onLoginSuccess={handleLoginSuccess}
          onLoginFailure={handleLoginFailure}
        />
        {isButtonVisible && (
          <button className="generate_btn" onClick={handleGenerate}>
            Continue
          </button>
        )}
      </div>
    </div>
    </>
  );
};

export default PropertyOwnership;
